import {
  BillingChargeType,
  TransactionStatus,
} from "@redotech/redo-billing-common/billing-display-labels";
import * as z from "zod/v4";

const dataModeSchema = z.enum(["all", "charges", "transactions"]);
export type DataMode = z.infer<typeof dataModeSchema>;

export const billingChargeTypeSchema = z.nativeEnum(BillingChargeType);

export const transactionStatusSchema = z.nativeEnum(TransactionStatus);

const billingChargeSchema_ = z.object({
  recordType: z.literal("charge"),
  id: z.string(),
  idempotencyKey: z.string(),
  billId: z.string().nullish(),
  type: billingChargeTypeSchema,
  chargeSubtype: z.string().nullish(),
  amount: z.number(),
  currency: z.string(),
  startTime: z.date(),
  endTime: z.date(),
  createdAt: z.date(),
  orderId: z.string().nullish(),
  returnId: z.string().nullish(),
  campaignId: z.string().nullish(),
  shipmentId: z.string().nullish(),
  carrierFeesMicros: z.number().nullish(),
  includedCount: z.number().nullish(),
  overageCount: z.number().nullish(),
  orderCount: z.number().nullish(),
  smsCount: z.number().nullish(),
  mmsCount: z.number().nullish(),
  recoveredRevenue: z.number().nullish(),
});
export const billingChargeSchema: z.ZodType<BillingChargeSchema> =
  billingChargeSchema_;
export interface BillingChargeSchema
  extends z.infer<typeof billingChargeSchema_> {}

const billingTransactionSchema_ = z.object({
  recordType: z.literal("transaction"),
  id: z.string(),
  idempotencyKey: z.string(),
  billId: z.string().nullish(),
  amount: z.number(),
  currency: z.string(),
  status: transactionStatusSchema,
  shopifyChargeId: z.string().nullish(),
  stripeChargeId: z.string().nullish(),
  stripeRefundId: z.string().nullish(),
  failureMessage: z.string().nullish(),
  createdAt: z.date(),
  paymentMethodId: z.string().nullish(),
});
export const billingTransactionSchema: z.ZodType<BillingTransactionSchema> =
  billingTransactionSchema_;
export interface BillingTransactionSchema
  extends z.infer<typeof billingTransactionSchema_> {}

const billingRecordSchema = z.union([
  billingChargeSchema,
  billingTransactionSchema,
]);
export type BillingRecord = z.infer<typeof billingRecordSchema>;

const getBillingChargesAndTransactionsInput_ = z.object({
  dataMode: dataModeSchema.optional().default("all"),
  pageSize: z.number().optional().default(50),
  offset: z.number().optional().default(0),
  sortKey: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional().default("desc"),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  startInclusive: z.boolean().optional(),
  endInclusive: z.boolean().optional(),
  chargeTypes: z.array(billingChargeTypeSchema).optional(),
  transactionStatuses: z.array(transactionStatusSchema).optional(),
  billId: z.string().optional(),
});
export const getBillingChargesAndTransactionsInput: z.ZodType<GetBillingChargesAndTransactionsInput> =
  getBillingChargesAndTransactionsInput_;
export interface GetBillingChargesAndTransactionsInput
  extends z.infer<typeof getBillingChargesAndTransactionsInput_> {}

const getBillingChargesAndTransactionsOutput_ = z.object({
  data: z.array(billingRecordSchema),
  total: z.number(),
  totalAmount: z.number(),
  nextOffset: z.number().nullish(),
});
export const getBillingChargesAndTransactionsOutput: z.ZodType<GetBillingChargesAndTransactionsOutput> =
  getBillingChargesAndTransactionsOutput_;
export interface GetBillingChargesAndTransactionsOutput
  extends z.infer<typeof getBillingChargesAndTransactionsOutput_> {}

export const getBillingChargesAndTransactions = {
  name: "getBillingChargesAndTransactions" as const,
  input: getBillingChargesAndTransactionsInput,
  output: getBillingChargesAndTransactionsOutput,
};
